import { LoadingButton } from "@mui/lab";
import {
    Box,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { FormFieldErrors, hasErrors, useForm, validateMandatoryField } from "../../utils/form";
import { useUserInfo } from "../../utils/security";

export const useFinancialSettingsForm = (options) => {
    const { userInfo, updateUserInfo } = useUserInfo();

    const onSubmit = useCallback(
        async ({ transactionsTaxSupport, ...data }, _hints, { setErrors }) => {
            await updateUserInfo({ ...userInfo, financesSettings: data, transactionsTaxSupport });
            options?.onSuccess?.();
        },
        [updateUserInfo, userInfo, options]
    );

    const { values, handleChange, submitting, setValues, handleSubmit, errors } = useForm({
        validate: (values, errors) => {
            validateMandatoryField(values.name, "name", errors);
        },
        onSubmit,
    });
    useEffect(() => {
        setValues({
            ...userInfo.financesSettings,
            transactionsTaxSupport: userInfo.transactionsTaxSupport,
        });
    }, [setValues, userInfo.financesSettings, userInfo.transactionsTaxSupport]);

    const disabled = submitting;

    return { values, handleChange, handleSubmit, errors, disabled };
};

export const FinancesSettingsFields = ({ form, vertical, children }) => {
    const { values, handleChange, errors, disabled } = form;

    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("md"));

    return (
        <Box mb={4}>
            <Stack
                direction={vertical || !matches ? "column-reverse" : "row"}
                spacing={vertical || !matches ? 2 : 4}
                divider={matches ? <Divider orientation="vertical" flexItem /> : undefined}>
                <Box sx={{ flex: 1 }}>
                    <Box>
                        <Box mb={2}>
                            <TextField
                                label="Název společnosti"
                                value={values.name || ""}
                                onChange={handleChange}
                                name="name"
                                size="big"
                                disabled={disabled}
                                required
                                fullWidth
                                margin="normal"
                                error={hasErrors(errors, "name")}
                                helperText={<FormFieldErrors errors={errors} field="name" />}
                            />
                        </Box>
                        <Box mb={2}>
                            <Stack direction="row" spacing={4}>
                                <TextField
                                    label="IČ"
                                    value={values.idNumber || ""}
                                    onChange={handleChange}
                                    name="idNumber"
                                    disabled={disabled}
                                    fullWidth
                                    margin="normal"
                                />
                                <TextField
                                    label="DIČ"
                                    value={values.taxIdNumber || ""}
                                    onChange={handleChange}
                                    name="taxIdNumber"
                                    disabled={disabled}
                                    fullWidth
                                    margin="normal"
                                />
                            </Stack>
                        </Box>
                    </Box>
                    <Box>
                        <TextField
                            label="Sídlo společnosti"
                            value={values.address || ""}
                            onChange={handleChange}
                            name="address"
                            disabled={disabled}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={5}
                        />
                    </Box>
                    <Box my={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={values.transactionsTaxSupport || false}
                                    onChange={handleChange}
                                    name="transactionsTaxSupport"
                                />
                            }
                            label="Evidovat daň na finančních transakcích (DPH apod)"
                            disabled={disabled}
                        />
                    </Box>
                    {children}
                </Box>
                <Box sx={{ flex: vertical ? 0 : 0.35 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                        Zde vyplněné informace se budou zobrazovat na fakturách a dalších
                        dokumentech spravovaných tímto uživatelským účtem.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Pokud chcete evidovat DPH (případně další daně) na finančních transakcích,
                        zaškrtněte volbu "evidovat daň na finančních transakcích".
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};

export const FinancesSettingsForm = ({ ...props }) => {
    const form = useFinancialSettingsForm();
    return (
        <form onSubmit={form.handleSubmit} noValidate>
            <FinancesSettingsFields {...props} form={form}>
                <Box sx={{ textAlign: "right" }}>
                    <LoadingButton
                        type="submit"
                        loading={form.disabled}
                        variant="contained"
                        size="large">
                        Uložit
                    </LoadingButton>
                </Box>
            </FinancesSettingsFields>
        </form>
    );
};
